import { createContext, useContext, useMemo, type ReactNode } from 'react';

import { LANDING_COPY, type LandingCopy, type LocaleKey } from '@/content/landingCopy';

type Locale = LocaleKey;

type LocaleContextValue = {
  locale: Locale;
  direction: 'ltr';
  setLocale: (_locale: Locale) => void;
  t: <Key extends keyof LandingCopy>(key: Key) => LandingCopy[Key];
  copy: LandingCopy;
};

const LocaleContext = createContext<LocaleContextValue | undefined>(undefined);

const ENGLISH_COPY = LANDING_COPY.en;

export function LocaleProvider({ children }: { children: ReactNode }) {
  const value = useMemo<LocaleContextValue>(
    () => ({
      locale: 'en',
      direction: 'ltr',
      setLocale: () => {},
      copy: ENGLISH_COPY,
      t: (key) => ENGLISH_COPY[key],
    }),
    [],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocale must be used within LocaleProvider');
  }
  return context;
}

export type { Locale, LandingCopy };
